import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { Project, ProjectWithPermissions, CreateProjectRequest } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';
import {
  addAnonymousProjectId,
  removeAnonymousProjectId,
  getAnonymousProjectIds,
} from '../utils/anonymousProjects';

interface UseProjectsReturn {
  projects: Project[];
  loading: boolean;
  error: string | null;
  fetchProjects: () => Promise<void>;
  fetchAnonymousProjects: () => Promise<Project[]>;
  fetchTeamProjects: (teamId: string) => Promise<void>;
  getProject: (projectId: string) => Promise<ProjectWithPermissions>;
  createProject: (data: CreateProjectRequest) => Promise<Project>;
  createTeamProject: (teamId: string, data: CreateProjectRequest) => Promise<Project>;
  createAnonymousProject: (name?: string) => Promise<Project>;
  updateProject: (projectId: string, data: { name?: string }) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
  claimProject: (projectId: string, data?: { name?: string; subdomain?: string }) => Promise<Project>;
  transferProject: (projectId: string, target: string) => Promise<Project>;
  renameSubdomain: (projectId: string, subdomain: string) => Promise<Project>;
  clearProjects: () => void;
}

export function useProjects(): UseProjectsReturn {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const json = await api.get<{ data: Project[] }>('/api/projects');
      setProjects(json.data || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch anonymous projects stored in localStorage
  const fetchAnonymousProjects = useCallback(async (): Promise<Project[]> => {
    const anonymousIds = getAnonymousProjectIds();
    if (anonymousIds.length === 0) return [];

    const projects: Project[] = [];

    await Promise.all(
      anonymousIds.map(async (id) => {
        try {
          const json = await api.get<{ data: Project }>(`/api/projects/${id}`);
          projects.push(json.data);
        } catch {
          // Project no longer exists or fetch failed, remove from localStorage
          removeAnonymousProjectId(id);
        }
      })
    );

    return projects;
  }, []);

  // Team-scoped equivalent of fetchProjects; replaces local state with the
  // team's projects (the caller is expected to only call one of the two per view).
  const fetchTeamProjects = useCallback(async (teamId: string) => {
    setLoading(true);
    setError(null);

    try {
      const json = await api.get<{ data: Project[] }>(`/api/teams/${teamId}/projects`);
      setProjects(json.data || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch team projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const getProject = useCallback(async (projectId: string): Promise<ProjectWithPermissions> => {
    const json = await api.get<{ data: ProjectWithPermissions }>(`/api/projects/${projectId}`);
    return json.data;
  }, []);

  const createProject = useCallback(async (data: CreateProjectRequest): Promise<Project> => {
    try {
      const json = await api.post<{ data: Project }>('/api/projects', data);
      const newProject = json.data;
      setProjects(prev => [...prev, newProject]);
      toast.success('Project created');
      return newProject;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create project';
      toast.error(message);
      throw err;
    }
  }, []);

  const createTeamProject = useCallback(async (teamId: string, data: CreateProjectRequest): Promise<Project> => {
    try {
      const json = await api.post<{ data: Project }>(`/api/teams/${teamId}/projects`, data);
      const newProject = json.data;
      setProjects(prev => [...prev, newProject]);
      toast.success('Project created');
      return newProject;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create project';
      toast.error(message);
      throw err;
    }
  }, []);

  const createAnonymousProject = useCallback(async (name?: string): Promise<Project> => {
    try {
      const json = await api.post<{ data: Project }>('/api/projects/anonymous', { name });
      const newProject = json.data;

      // Store in localStorage so user can access it later
      addAnonymousProjectId(newProject.id);

      setProjects(prev => [...prev, newProject]);
      toast.success('Project created');

      return newProject;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create project';
      toast.error(message);
      throw err;
    }
  }, []);

  const updateProject = useCallback(async (projectId: string, data: { name?: string }): Promise<Project> => {
    // Store previous state for rollback
    const previousProjects = projects;

    // Optimistic update
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...data } : p));

    try {
      const json = await api.put<{ data: Project }>(`/api/projects/${projectId}`, data);
      const updatedProject = json.data;

      // Update with server response to ensure consistency
      setProjects(prev => prev.map(p => p.id === projectId ? updatedProject : p));
      toast.success('Project updated');

      return updatedProject;
    } catch (err) {
      // Rollback on error
      setProjects(previousProjects);
      const message = err instanceof ApiError ? err.message : 'Failed to update project';
      toast.error(message);
      throw err;
    }
  }, [projects]);

  const deleteProject = useCallback(async (projectId: string): Promise<void> => {
    // Store previous state for rollback
    const previousProjects = projects;

    // Optimistic update
    setProjects(prev => prev.filter(p => p.id !== projectId));

    try {
      await api.del(`/api/projects/${projectId}`);
      // Remove from localStorage if it was an anonymous project
      removeAnonymousProjectId(projectId);
      toast.success('Project deleted');
    } catch (err) {
      // Rollback on error
      setProjects(previousProjects);
      const message = err instanceof ApiError ? err.message : 'Failed to delete project';
      toast.error(message);
      throw err;
    }
  }, [projects]);

  const claimProject = useCallback(async (projectId: string, data?: { name?: string; subdomain?: string }): Promise<Project> => {
    try {
      const json = await api.post<{ data: Project }>(`/api/projects/${projectId}/claim`, data || {});
      const claimedProject = json.data;

      // Remove from anonymous localStorage since it's now owned
      removeAnonymousProjectId(projectId);

      // Update local state
      setProjects(prev => prev.map(p => p.id === projectId ? claimedProject : p));
      toast.success('Project claimed to your account');

      return claimedProject;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to claim project';
      toast.error(message);
      throw err;
    }
  }, []);

  const transferProject = useCallback(async (projectId: string, target: string): Promise<Project> => {
    try {
      const json = await api.post<{ data: Project }>(`/api/projects/${projectId}/transfer`, { target });
      const transferred = json.data;
      // The project may leave the current view's scope (personal ↔ team); reflect
      // the new state where it's still listed and drop it where it no longer belongs.
      setProjects(prev => prev.map(p => p.id === projectId ? transferred : p));
      toast.success(target === 'personal' ? 'Project moved to personal' : 'Project transferred to team');
      return transferred;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to transfer project';
      toast.error(message);
      throw err;
    }
  }, []);

  // Not optimistic: the old URL keeps working until the server has moved the
  // project. Failures are left to the caller, which shows them inline.
  const renameSubdomain = useCallback(async (projectId: string, subdomain: string): Promise<Project> => {
    const json = await api.put<{ data: Project }>(`/api/projects/${projectId}/subdomain`, { subdomain });
    const renamed = json.data;
    setProjects(prev => prev.map(p => p.id === projectId ? renamed : p));
    toast.success('Subdomain changed');
    return renamed;
  }, []);

  const clearProjects = useCallback(() => {
    setProjects([]);
  }, []);

  return {
    projects,
    loading,
    error,
    fetchProjects,
    fetchAnonymousProjects,
    fetchTeamProjects,
    getProject,
    createProject,
    createTeamProject,
    createAnonymousProject,
    updateProject,
    deleteProject,
    claimProject,
    transferProject,
    renameSubdomain,
    clearProjects,
  };
}
